/**
 * /admin/claimant/:email — drill-down on one claimant's full claim history.
 * Linked from the Approvals queues, Reports, and All Claims rows.
 */
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";
import StatusBadge from "../../components/StatusBadge";
import { gbp, dateShort } from "../../lib/format";

export default function ClaimantProfile() {
  const { oid = "" } = useParams<{ oid: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ["claimant", oid],
    queryFn: () => (api.admin as any).claimantProfile(oid) as Promise<{
      profile: {
        email: string;
        display_name: string;
        oid?: string;
        manager?: { displayName: string; email: string } | null;
        department?: string | null;
        jobTitle?: string | null;
        officeLocation?: string | null;
        city?: string | null;
        employee_number?: string | null;
      };
      stats: {
        lifetime_count: number;
        lifetime_amount: number;
        paid_amount: number;
        in_flight_amount: number;
        draft_count: number;
        submitted_count: number;
        manager_approved_count: number;
        payroll_approved_count: number;
        paid_count: number;
        rejected_count: number;
        paid_12m: number;
      };
      claims: any[];
    }>,
    enabled: !!oid,
  });

  if (isLoading) return <div className="text-slate-500">Loading…</div>;
  if (error) return <div className="text-red-600 text-sm">{(error as Error).message}</div>;
  if (!data) return null;

  const { profile, stats, claims } = data;

  return (
    <div className="space-y-6">
      <Link to="/admin/all-claims" className="text-sm text-slate-500 hover:text-slate-700">← Back to all claims</Link>

      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-semibold text-liaise-navy">{profile.display_name}</h1>
            <div className="text-slate-500 text-sm mt-1">{profile.email}</div>
          </div>
          {profile.employee_number && (
            <div className="text-right">
              <div className="text-xs text-slate-500">Employee #</div>
              <div className="font-mono text-slate-700">{profile.employee_number}</div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 text-sm">
          {profile.jobTitle && (
            <div><span className="text-slate-500 block text-xs">Role</span><span>{profile.jobTitle}</span></div>
          )}
          {profile.department && (
            <div><span className="text-slate-500 block text-xs">Department</span><span>{profile.department}</span></div>
          )}
          {(profile.officeLocation || profile.city) && (
            <div><span className="text-slate-500 block text-xs">Location</span><span>{profile.officeLocation ?? profile.city}</span></div>
          )}
          {profile.manager && (
            <div>
              <span className="text-slate-500 block text-xs">Reports to</span>
              <span>{profile.manager.displayName}</span>
              <div className="text-xs text-slate-500">{profile.manager.email}</div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card label="Total claims" value={String(stats.lifetime_count ?? 0)} />
        <Card label="Lifetime spend" value={gbp(Number(stats.lifetime_amount ?? 0))} sub="excludes drafts and rejected" />
        <Card label="Paid" value={gbp(Number(stats.paid_amount ?? 0))} sub={`${stats.paid_count ?? 0} claim${(stats.paid_count ?? 0) === 1 ? "" : "s"}`} />
        <Card label="In flight" value={gbp(Number(stats.in_flight_amount ?? 0))} sub="awaiting approval or payment" muted />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="font-medium text-liaise-navy">All claims ({claims.length})</h2>
        </div>
        {claims.length === 0 ? (
          <div className="px-6 py-12 text-center text-slate-500">No claims yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-6 py-3 font-medium">Number</th>
                <th className="px-6 py-3 font-medium">Cost centre</th>
                <th className="px-6 py-3 font-medium">Approver</th>
                <th className="px-6 py-3 font-medium">Lines</th>
                <th className="px-6 py-3 font-medium text-right">Total</th>
                <th className="px-6 py-3 font-medium">Created</th>
                <th className="px-6 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {claims.map((c: any) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="px-6 py-3">
                    <Link to={`/approvals/${c.id}?stage=manager`} className="text-liaise-navy font-medium hover:underline">
                      {c.request_number}
                    </Link>
                  </td>
                  <td className="px-6 py-3 text-slate-600">{c.cost_centre_name}</td>
                  <td className="px-6 py-3 text-slate-600 text-xs">
                    {c.manager_email ?? <span className="text-slate-400 italic">none</span>}
                    {c.manager_overridden && (
                      <span
                        title={`Set by ${c.manager_override_set_by_email ?? "unknown"}${c.manager_override_reason ? `\n\n${c.manager_override_reason}` : ""}`}
                        className="ml-1 bg-amber-100 text-amber-800 text-[10px] font-medium px-1.5 py-0.5 rounded cursor-help"
                      >
                        ⚠ overridden
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-slate-600">{c.line_count}</td>
                  <td className="px-6 py-3 text-right font-medium">{gbp(Number(c.total_amount))}</td>
                  <td className="px-6 py-3 text-slate-600">{dateShort(c.created_at)}</td>
                  <td className="px-6 py-3"><StatusBadge status={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Card({ label, value, sub, muted }: { label: string; value: string; sub?: string; muted?: boolean }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl p-5 shadow-sm ${muted ? "opacity-90" : ""}`}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-semibold text-liaise-navy mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}
